import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ChevronLeft, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { Gate } from "@/components/wallet/Gate";
import { useWallet } from "@/lib/wallet/store";
import { addressFingerprint } from "@/lib/wallet/format";

export const Route = createFileRoute("/receive")({
  staticData: { sitemap: false },
  head: () => ({
    meta: [
      { title: "Receive — Cobalt Wallet" },
      {
        name: "description",
        content:
          "Share your XRP Ledger address as a QR code, with an optional destination tag, to receive XRP and tokens.",
      },
      { property: "og:title", content: "Receive — Cobalt Wallet" },
      {
        property: "og:description",
        content: "Your XRPL receiving address and QR code, generated on device.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: () => (
    <Gate>
      <ReceiveScreen />
    </Gate>
  ),
});

function ReceiveScreen() {
  const { address } = useWallet();
  const [tag, setTag] = useState("");
  const [qr, setQr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const payload = address
    ? tag.trim()
      ? `${address}?dt=${tag.trim()}`
      : address
    : "";

  useEffect(() => {
    let cancelled = false;
    if (!payload) return;
    void (async () => {
      const QRCode = (await import("qrcode")).default;
      const url = await QRCode.toDataURL(payload, {
        margin: 1,
        width: 512,
        color: { dark: "#e8e6e1", light: "#0b0d1000" },
      });
      if (!cancelled) setQr(url);
    })();
    return () => {
      cancelled = true;
    };
  }, [payload]);

  return (
    <div className="animate-rise space-y-4">
      <div className="flex items-center gap-2">
        <Link
          to="/"
          aria-label="Back to home"
          className="grid size-8 place-items-center rounded-lg border border-border text-muted"
        >
          <ChevronLeft className="size-4" />
        </Link>
        <h1 className="text-[18px] font-medium tracking-tight">Receive</h1>
      </div>

      <section className="panel p-5">
        <div className="mx-auto grid size-56 place-items-center rounded-xl border border-border bg-background p-3">
          {qr ? (
            <img src={qr} alt="QR code for your XRPL address" className="size-full" />
          ) : (
            <p className="label-instrument">generating…</p>
          )}
        </div>

        <p className="label-instrument mt-5">Your address</p>
        <p className="readout mt-1 break-all text-[13px] leading-relaxed">{address}</p>
        <p className="readout mt-2 text-[10px] text-faint">
          Fingerprint{" "}
          <span className="text-accent">{addressFingerprint(address ?? "")}</span> — read this out
          loud to confirm you shared the right address.
        </p>

        <button
          type="button"
          onClick={async () => {
            if (!address) return;
            await navigator.clipboard.writeText(payload);
            setCopied(true);
            toast.success("Address copied");
          }}
          className="readout mt-4 flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-accent text-[11px] uppercase tracking-[0.15em] text-accent-ink"
        >
          {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />} Copy
        </button>
      </section>

      <section className="panel p-4">
        <p className="label-instrument">Destination tag (optional)</p>
        <input
          value={tag}
          onChange={(e) => setTag(e.target.value.replace(/[^0-9]/g, ""))}
          inputMode="numeric"
          placeholder="e.g. an invoice number"
          className="readout mt-1.5 h-11 w-full rounded-lg border border-border bg-background px-3 text-[13px] outline-none placeholder:text-faint focus:border-accent"
        />
        <p className="readout mt-2 text-[10px] leading-relaxed text-faint">
          Tags help exchanges and services attribute an incoming payment. Anything you receive lands
          in this account either way.
        </p>
      </section>
    </div>
  );
}
